import type { QuoteDocument, Auth0User, OptionsDocument, QuoteOptions } from 'paintscout'
import get from 'lodash/get'
import pick from 'lodash/pick'
import { uuid, getOwnerFromUser } from '../../../util'
import { generateProductDescription } from '../../util/generate-product-description/generate-product-description'
import { ACCOUNT_MANAGED_WORK_ORDER_OPTIONS } from '../../util/get-options'

/**
 * Creates a copy of the quote object
 *
 * A copy is a brand new quote, so the account-managed work-order display preferences
 * are re-resolved from current account settings rather than inherited from the source
 * quote's snapshot — otherwise a value baked in before the account setting changed
 * silently wins forever (#22179, same class of bug as #20749 for templates).
 * Everything else on `quote.options` (prices, rounding, service fees, deposit,
 * surcharge, add-button positions, ...) reflects the copied content and is preserved.
 *
 * @param quote
 * @param user
 * @param options - account OptionsDocument. When omitted, `quote.options` is copied verbatim.
 */
export const copyQuote = (args: {
  quote: QuoteDocument
  user: Auth0User
  options?: OptionsDocument
}): QuoteDocument => {
  // Callers can hand us an undefined quote when their query never resolved — CopyQuoteDialog reads
  // `data?.quote`, which is `any` because the schema types `quote(id: String!)` as JSON, so nothing
  // catches it at compile time. Without this the destructure below throws a minified
  // `Cannot destructure property '_id' of 'e.quote'` that says nothing useful (#22824).
  if (!args?.quote) {
    throw new Error('copyQuote requires a quote')
  }

  const { user, options } = args
  const {
    _id,
    _rev,
    number: _quoteNumber,
    sentReports: _sentReports,
    child_invoice: _child_invoice,
    child_invoice_number: _child_invoice_number,
    child_invoice_numbers: _child_invoice_numbers,
    deal_id: _deal_id,
    parent_quote,
    parent_quote_number,
    dates: _dates,
    followUps: _followUps,
    lastMessage: _lastMessage,
    ...quote
  } = args.quote
  const owner = getOwnerFromUser(user)
  const now = Date.now()

  const copiedOptions = quote.options ? resetAccountManagedOptions({ quoteOptions: quote.options, options }) : undefined
  const quoteWithCopiedOptions = {
    ...quote,
    ...(copiedOptions ? { options: copiedOptions } : {})
  } as QuoteDocument

  return {
    ...quote,
    _id: uuid(),
    ...(copiedOptions ? { options: copiedOptions } : {}),
    ...regenerateProductString({ quote: quoteWithCopiedOptions, options }),
    ...(quote.is_invoice && {
      parent_quote,
      parent_quote_number
    }),
    owner,
    assignedUsers: {
      [owner.id]: {
        assigned: true
      }
    },
    quote_date: now,
    dates: {
      created: now,
      updated: now,
      client: {},
      staff: {}
    },
    status: {
      value: 'draft'
    },
    urls: {},
    followUps: {},
    copyOf: _id,
    source: null,
    sources: quote.sources
      ? {
          ...quote?.sources,
          ...(quote?.sources?.['serviceTitan']
            ? {
                serviceTitan: {
                  ...quote?.sources?.['serviceTitan'],
                  estimateId: null
                }
              }
            : {})
        }
      : null,
    child_invoices: [],
    depositInfo: null,
    calculationLock: false,
    followUpsDisabled: false,
    contact: {
      ...quote.contact,
      wisetackLoanApplication: null
    }
  }
}

/**
 * Re-renders the copied quote's work-order Product Description from the copy's own options.
 *
 * `productString.default` is baked text, and `calculateQuote` (the only writer) is not reached by
 * every copy path — `CopyQuoteDialog` recalculates only when the caller passes `calculate: true`,
 * and `QuoteTemplateOptionsContainer` never does. Without this, a copy carries the source's
 * rendered material costs while its own options say to hide them (#22179 / ENG-1130).
 *
 * The user's own edit (`custom` / `useCustom`) is preserved. Without an account OptionsDocument the
 * account value cannot be resolved, so the stored string is left alone.
 */
function regenerateProductString(args: { quote: QuoteDocument; options?: OptionsDocument }): Partial<QuoteDocument> {
  const { quote, options } = args

  if (!options || !quote.productString) {
    return {}
  }

  return {
    productString: {
      ...quote.productString,
      default: generateProductDescription({ quote, options })
    }
  }
}

/**
 * Replaces the account-managed work-order display preferences on a copied quote's options
 * with the account's current values. Keys the account doesn't define are left as-is, so the
 * quote keeps falling back the way `getQuoteOptions` would resolve it — which is also what
 * happens when `options.options.quotes` is missing entirely and `accountOptions` lands as `{}`.
 */
function resetAccountManagedOptions(args: { quoteOptions: QuoteOptions; options?: OptionsDocument }): QuoteOptions {
  const { quoteOptions, options } = args

  if (!options) {
    return quoteOptions
  }

  const accountOptions = get(options, 'options.quotes', {}) as Partial<QuoteOptions>
  const definedKeys = ACCOUNT_MANAGED_WORK_ORDER_OPTIONS.filter((key) => typeof accountOptions[key] !== 'undefined')

  return { ...quoteOptions, ...pick(accountOptions, definedKeys) }
}
